/* eslint-disable security/detect-object-injection */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Divider, Typography} from '@mui/material';
import './DomainAdminDashboard.scss';
import {ArrToggle} from '../../common/arr-toggle/ArrToggle';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrButton from '../../common/arr-button/ArrButton';
import {ReactComponent as AddIcon} from '../../../assets/images/add.svg';
import {ReactComponent as AddFieldSortIcon} from '../../../assets/images/add-fields-sort.svg';
import {ReactComponent as SaveIcon} from '../../../assets/images/save.svg';
import {ReactComponent as UploadIcon} from '../../../assets/images/upload.svg';
import {ReactComponent as NoDataIcon} from '../../../assets/images/no-data-found.svg';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {GridColDef, GridSortDirection, GridSortModel} from '@mui/x-data-grid';
import {domainAdminDashboardColumnsMapping} from '../dashboardUtils';
import Grid from '@mui/material/Grid';
import ActiveProjectsWidget from '../dashboard-widgets/active-projects-widget/ActiveProjectsWidget';
import ActiveProjectUtilizationWidget from '../dashboard-widgets/active-project-utilization-widget/ActiveProjectUtilizationWidget';
import UsersInActiveProjectsWidget from '../dashboard-widgets/users-in-active-projects-widget/UsersInActiveProjectsWidget';
import {CustomizeTagWidget} from '../dashboard-widgets/customise-tag-widget/CustomizeTagWidget';
import {CustomizeReviewerProductivityWidget} from '../dashboard-widgets/customize-reviewer-productivity-widget/CustomizeReviewerProductivityWidget';
import {CustomizeReviewerProgressWidget} from '../dashboard-widgets/customize-reviewer-progress-widget/CustomizeReviewerProgressWidget';
import {
  useDeactivateProject,
  useDomainAdminGridConfig,
  useDomainAdminGridData,
  useExportToExcel,
  useSaveColumnDetails,
  useChangeRoleToRMU,
} from '../../../hooks/dashboard/useDashboard';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {DomainDashboardColumnsMap} from '../dashboardTypes';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import ArrMenu, {ArrMenuHandle} from '../../common/arr-menu/ArrMenu';
import ArrCheckbox from '../../common/arr-checkbox/ArrCheckbox';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {useNavigate} from 'react-router-dom';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ReportTypes, ToastType} from '../../../shared/constants';
import ArrToast from '../../common/arr-toast/ArrToast';
import ArrModal from '../../common/arr-modal/ArrModal';
import {RootState} from '../../../redux/store';
import {useDispatch, useSelector} from 'react-redux';
import {setAuthenticatedUser} from '../../../redux/slice/authenticatedUserSlice';
import {resetProjectForm} from '../../../redux/slice/projectFormSlice';
import {setDomainData} from '../../../redux/slice/domainDataSlice';
import useDebounce from '../../../hooks/utils/useDebounce';
import dayjs from 'dayjs';
import CellWrapper from '../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';

const SIGNALR_EVENTS: string[] = [
  'ProjectCreated',
  'UpdateAnalyticsState',
  'NotificationCount',
  'BulkSavedSearchEvent',
  'BulkUploadSaveSearchEvent',
  'ProductionSummaryPayload',
];

const DomainAdminDashboard: React.FC = () => {
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const [isToggleActive, setIsToggleActive] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'projectLabel',
      sort: 'asc',
    },
  ]);

  const [visibleFields, setVisibleFields] = useState<string[]>([]);
  const [allColumns, setAllColumns] = useState<DomainDashboardColumnsMap[]>([]);
  const menuRef1 = useRef<ArrMenuHandle | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastStatuses, setToastStatuses] = useState<ToastType[]>([]);
  const handleToastClose = () => {
    setShowToast(false);
    setToastStatuses([]);
  };

  const [open, setOpen] = useState(false);
  const [projectToDeactivate, setProjectToDeactive] = useState<
    number | undefined
  >();

  const {mutate: saveColumnDetails} = useSaveColumnDetails();
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const handleToggleColumn = (field: string) => {
    setVisibleFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const filteredColumns = allColumns.filter((col) =>
    visibleFields.includes(col.id)
  );

  const [refreshTime, setRefreshTime] = useState('');

  const {
    data: gridConfig,
    isLoading,
    refetch: refetchGridConfig,
    dataUpdatedAt,
  } = useDomainAdminGridConfig();

  const {mutate: deactivate} = useDeactivateProject();
  const [toastMessage, setToastMessage] = useState('');
  const [reportToastMessage, setReportToastMessage] = useState('');

  const {mutate: exportToExcel} = useExportToExcel();
  const {mutate: changeRoleToRMU} = useChangeRoleToRMU();
  const dispatch = useDispatch();

  const entityId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.entityId
  );
  const handleExport = () => {
    const payload = {
      EntityID: entityId,
      ReportTypeCode: ReportTypes.DOMAINDASHBOARDXPORT,
      RowNumber: 0,
      projectLabel: '',
      pageSize: 1000,
      pageNumber: 1,
      sortingOrder: sortModel[0].sort,
      sortingColumn: sortModel[0].field,
      isActive: !isToggleActive,
    };
    exportToExcel(JSON.stringify(payload), {
      onSuccess: () => {
        setShowToast(true);
        setToastStatuses((prev) => [...prev, ToastType.Success]);
        setReportToastMessage(t('domainDashboard.exportReportSuccess'));
      },
    });
  };

  useEffect(() => {
    if (gridConfig?.domainDashboardRefreshTime) {
      setRefreshTime(gridConfig.domainDashboardRefreshTime);
    }
    if (gridConfig?.sortingColumn) {
      setSortModel([
        {
          field: gridConfig.sortingColumn,
          sort: gridConfig.isAscending as GridSortDirection,
        },
      ]);
    }
    if (gridConfig?.sourceColumnList) {
      setVisibleFields(gridConfig.sourceColumnList);
      setAllColumns(
        gridConfig.availableAllColumns.map((column) => {
          const columnMap = domainAdminDashboardColumnsMapping.find(
            (c) => c.id === column.columnName
          );
          return {
            id: column.columnName,
            key: columnMap?.key ?? '',
            tooltipKey: columnMap?.tooltipKey ?? '',
            type: column.baseDataTypeCode,
          };
        })
      );
    }
  }, [gridConfig]);

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebounce(searchInput, 300);

  const {
    data: gridData,
    refetch: refetchGridData,
    isLoading: gridLoading,
  } = useDomainAdminGridData({
    projectLabel: debouncedSearchInput,
    pageSize: itemsPerPage,
    pageNumber: pageNumber,
    sortingOrder: sortModel[0].sort,
    sortingColumn: sortModel[0].field,
    isActive: !isToggleActive,
    entityId: entityId,
  });
  // ------

  const navigate = useNavigate();
  const [showCustomizeTagWidget, setShowCustomizeTagWidget] = useState(false);
  const [
    showCustomizeReviewerProductivity,
    setShowCustomizeReviewerProductivity,
  ] = useState(false);
  const [showCustomizeReviewerProgress, setShowCustomizeReviewerProgress] =
    useState(false);

  // Update widgets on selected SignalR notification
  useEffect(() => {
    const {payload} = lastEvent || {};
    if (payload?.type && SIGNALR_EVENTS.includes(payload.type)) {
      refetchGridConfig();
      refetchGridData();
    }
  }, [lastEvent]);
  // ------

  // Update grid columns on grid data
  useEffect(() => {
    if (allColumns?.length && gridData) {
      const filtered =
        gridData?.data.map((row) => {
          const rowObject: Record<string, string> = {};
          row.forEach((value, index) => {
            rowObject[allColumns[index]?.id] = value;
          });
          return rowObject;
        }) ?? [];
      setRows(filtered);
    }
  }, [gridData, allColumns]);
  // ------

  // Reset Page number on search
  useEffect(() => {
    if (debouncedSearchInput) {
      setPageNumber(1);
    }
  }, [debouncedSearchInput]);
  // ------

  const handleToggleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setIsToggleActive(checked);
  };
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value.trim();
      setSearchInput(query);
    },
    []
  );
  const onPageChangeHandler = (value: number) => {
    setPageNumber(value);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setSortModel(model);
    }
  };
  const deactivateProject = (projectId: number) => {
    setProjectToDeactive(projectId);
    setOpen(true);
  };

  const menuRefs = useRef<
    Record<string | number, React.RefObject<ArrMenuHandle | null>>
  >({});
  const getMenuRef = (projectId: string | number) => {
    if (!menuRefs.current[projectId]) {
      menuRefs.current[projectId] = React.createRef<ArrMenuHandle>();
    }
    return menuRefs.current[projectId];
  };

  const handleClose = () => {
    setOpen(false);
    setProjectToDeactive(undefined);
  };

  const deactivateConfirm = () => {
    deactivate(
      {projectId: projectToDeactivate, entityId: entityId},
      {
        onSuccess: () => {
          handleClose();
          setToastMessage(t('common.success'));
          refetchGridData();
          refetchGridConfig();
        },
      }
    );
  };

  const handleGoToProject = (projectId: number) => {
    changeRoleToRMU(
      {projectId: projectId},
      {
        onSuccess: (result) => {
          const mergedUser = {
            ...loginUserData,
            projectId: result?.projectId,
            securityGroupId: result?.securityGroupId,
            baseRoleId: result?.baseRoleId,
            roleId: result?.roleId,
            roleName: result?.roleName,
            landingPage: result?.landingPage,
          };
          dispatch(setAuthenticatedUser(mergedUser));
          navigate(result?.landingPage);
        },
      }
    );
  };

  const getDateInputFormat = (utcTime: string) => {
    const formats = ['YYYY/MM/DD hh:mm:ss A', 'YYYY/MM/DD hh:mm:ss.sss'];

    const matchedFormat = formats.find((format) =>
      dayjs.utc(utcTime, format, false).isValid()
    );

    return matchedFormat || formats[0];
  };

  const getLocalTime = (utcTime: string) => {
    const inputFormat = getDateInputFormat(utcTime);

    return dayjs(utcTime).isValid()
      ? dayjs
          .utc(utcTime, inputFormat, false)
          .local()
          .format('YYYY/MM/DD hh:mm:ss A')
      : '';
  };
  // -----

  const getUTCFormat = (utcTime: string) => {
    const inputFormat = getDateInputFormat(utcTime);

    return dayjs(utcTime).isValid()
      ? dayjs.utc(utcTime, inputFormat, false).format('YYYY/MM/DD hh:mm:ss A')
      : '';
  };
  // -----

  const getColumns = useMemo(() => {
    return filteredColumns.map((field) => {
      const column = allColumns.find((c) => c.id === field.id);
      return column
        ? {
            field: field.id,
            headerName: t(`${column.key}`),
            headerClassName: 'typo-text label2_size',
            flex: 1,
            width: 224,
            renderCell: (params) => {
              const date = getUTCFormat(params.row.LastUpdatedOn).split(' ');
              const projectCreatedOn = getLocalTime(
                params.row.ProjectCreatedOn
              );
              const projectLastLogin = getLocalTime(
                params.row.ProjectLastLoginActivity
              );
              const projectDocumentActivity = getLocalTime(
                params.row.ProjectLastDocumentActivity
              );
              const ref = getMenuRef(params.row.ProjectID);
              switch (column.id) {
                case 'ProjectLabel':
                  return (
                    <>
                      <ArrMenu
                        id="arr-menu-2"
                        ref={ref}
                        items={[
                          {
                            label: t('dashboard.activeProjects.goToProject'),
                            id: '1',
                          },
                          {
                            label: t('dashboard.activeProjects.deactivate'),
                            id: '2',
                          },
                        ]}
                        direction="bottom-left"
                        onItemClick={(item) => {
                          if (item.id === '1') {
                            handleGoToProject(params.row.ProjectID);
                          }
                          if (item.id === '2') {
                            deactivateProject(params.row.ProjectID);
                          }
                        }}
                      />
                      <ArrButton
                        variant="text"
                        className="link-button"
                        dataTestId={`link-button-${params.row.ProjectLabel}`}
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          if (params.row.IsActive === 'Active') {
                            ref.current?.open(e.currentTarget as HTMLElement);
                          }
                        }}
                      >
                        <CellWrapper
                          toolTipContent={params.row.ProjectLabel}
                          cellParams={params}
                          dataTestId={`tooltip-domain-admin-dashboard-project-label-${params.id}`}
                        >
                          <Typography
                            className="typo-text label3_size project-name-text"
                            // title={params.row.ProjectLabel}
                            data-testid={`domain-admin-dashboard-project-label-${params.id}`}
                          >
                            {params.row.ProjectLabel}
                          </Typography>
                        </CellWrapper>
                      </ArrButton>
                    </>
                  );
                case 'LastUpdatedOn':
                  return (
                    <div
                      data-testid={`domain-admin-dashboard-last-updated-date-time-${params.id}`}
                    >
                      <Typography
                        className="typo-text body3_size utc-date-text"
                        data-testid={`domain-admin-dashboard-typography-last-updated-date-${params.id}`}
                      >
                        {date[0]}
                      </Typography>
                      <Typography
                        className="typo-text body4_size utc-time-text"
                        data-testid={`domain-admin-dashboard-typography-last-uopdated-time-${params.id}`}
                      >
                        {`${date[1]} ${date[2]}`}
                      </Typography>
                    </div>
                  );
                case 'ProjectCreatedOn':
                  return (
                    <CellWrapper
                      toolTipContent={projectCreatedOn}
                      cellParams={params}
                      className="ellipsis-wrapper"
                      dataTestId={`domain-admin-dashboard-div-pco-${params.id}`}
                    >
                      <Typography
                        className="typo-text body3_size utc-date-text project-created-on-text"
                        // title={projectCreatedOn}
                        data-testid={`domain-admin-dashboard-typography-project-created-on-${params.id}`}
                      >
                        {projectCreatedOn}
                      </Typography>
                    </CellWrapper>
                  );
                case 'ProjectLastLoginActivity':
                  return (
                    <div
                      className="ellipsis-wrapper"
                      data-testid={`domain-admin-dashboard-div-plla${params.id}`}
                    >
                      <Typography
                        className="typo-text body3_size utc-date-text project-created-on-text"
                        title={projectLastLogin}
                        data-testid={`domain-admin-dashboard-typography-icolast-login-${params.id}n`}
                      >
                        {projectLastLogin}
                      </Typography>
                    </div>
                  );
                case 'ProjectLastDocumentActivity':
                  return (
                    <div
                      className="ellipsis-wrapper"
                      data-testid={`domain-admin-dashboard-div-plda-${params.id}`}
                    >
                      <Typography
                        className="typo-text body3_size utc-date-text project-created-on-text"
                        title={projectDocumentActivity}
                        data-testid={`domain-admin-dashboard-typography-last-document-${params.id}`}
                      >
                        {projectDocumentActivity}
                      </Typography>
                    </div>
                  );
                case 'ProjectCreatedBy':
                  return (
                    <div
                      className="ellipsis-wrapper"
                      data-testid={`domain-admin-dashboard-div-link-${params.id}`}
                    >
                      <CellWrapper
                        toolTipContent={params.row.ProjectCreatedBy}
                        cellParams={params}
                        dataTestId={`tooltip-domain-admin-dashboard-typography-created-by-${params.id}`}
                      >
                        <Typography
                          className="typo-text body3_size project-createdBy"
                          // title={params.row.ProjectCreatedBy}
                          data-testid={`domain-admin-dashboard-typography-created-by-${params.id}`}
                        >
                          {params.row.ProjectCreatedBy}
                        </Typography>
                      </CellWrapper>
                    </div>
                  );
                default:
                  return (
                    <Typography
                      className="typo-text body3_size utc-date-text"
                      data-testid={`domain-admin-dashboard-typography-container-${column.id}--${params.id}`}
                    >
                      {params.row[column.id]}
                    </Typography>
                  );
              }
            },
            renderHeader: () => {
              return (
                <Box className="grid-header">
                  <Typography
                    className="typo-text label2_size wrap-text"
                    data-testid={`domain-admin-dashboard-header-${column.key}`}
                  >
                    {t(`${column.key}`)}
                  </Typography>
                  {t(`${column.tooltipKey}`) && (
                    <ArrTooltipWrapper
                      trigger={<InfoIcon className="info-icon" />}
                    >
                      {t(column.tooltipKey)}
                    </ArrTooltipWrapper>
                  )}
                </Box>
              );
            },
          }
        : null;
    }) as GridColDef[];
  }, [filteredColumns, allColumns]);

  const NoRowsOverlay = () => (
    <Box className="no-project-data-found">
      <NoDataIcon />
      <Typography
        className="typo-text headline3_size no-project-text"
        data-testid={`domain-admin-dashboard-there-are-no-projects-to-display`}
      >
        There are no projects to display
      </Typography>
    </Box>
  );
  if (isLoading) {
    return <ArrLoadingSpinner />;
  }

  return (
    <Box className="domain-admin-dashboard">
      <div
        className="widget-section"
        data-testid={`domain-admin-dashboard-data-refreshed`}
      >
        <Typography
          className="typo-text body3_size refresh-text"
          data-testid={`domain-admin-dashboard-data-refreshed-text`}
        >
          {t('domainDashboard.dataRefreshed')} {refreshTime}
        </Typography>
        <div
          className="widgets"
          data-testid={`domain-admin-dashboard-div-input`}
        >
          <Grid container spacing={2}>
            <Grid size={{xs: 12, md: 4, lg: 4}}>
              <ActiveProjectsWidget
                updatedAt={dataUpdatedAt}
                entityId={entityId}
              />
            </Grid>
            <Grid size={{xs: 12, md: 4, lg: 4}}>
              <ActiveProjectUtilizationWidget
                updatedAt={dataUpdatedAt}
                entityId={entityId}
              />
            </Grid>
            <Grid size={{xs: 12, md: 4, lg: 4}}>
              <UsersInActiveProjectsWidget
                updatedAt={dataUpdatedAt}
                entityId={entityId}
              />
            </Grid>
          </Grid>
        </div>
      </div>
      <div
        className="grid-section"
        data-testid={`domain-admin-dashboard-display-inactive-add-new-project`}
      >
        <div
          className="filter-div"
          data-testid={`domain-admin-dashboard-display-inactive-add-new-project-btn`}
        >
          <div
            className="left-toggle-div"
            data-testid={`domain-admin-dashboard-display-inactive`}
          >
            <ArrToggle
              disabled={false}
              checked={isToggleActive}
              onChange={handleToggleChange}
              dataTestId="arr-active-toggle"
            />
            <Typography
              className="typo-text body3_size "
              data-testid={`domain-admin-dashboard-display-inactive-text`}
            >
              {t('domainDashboard.displayInactive')}
            </Typography>
          </div>
          <div
            className="search-box-div"
            data-testid={`domain-admin-dashboard-div-text`}
          >
            <ArrTextField
              sizeVariant="medium"
              className="search-box"
              type="search"
              placeholder="Search Projects"
              value={searchInput}
              onChange={handleSearchChange}
              dataTestId="domain-admin-dashboard-search"
            />
          </div>
          <div
            className="right-div"
            data-testid={`domain-admin-dashboard-add-new-project`}
          >
            <ArrButton
              type="submit"
              variant="primary"
              title="Drawer"
              size="small"
              dataTestId="new-project-button"
              startIcon={<AddIcon className="add-icon" />}
              onClick={() => {
                dispatch(resetProjectForm());
                dispatch(setDomainData([]));
                navigate('/Project/CreateProject');
              }}
            >
              {t('domainDashboard.addNewProject')}
            </ArrButton>
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              className="arr-divider"
            />
            <ArrIconButton
              icon={<UploadIcon className="upload-icon" />}
              onClick={handleExport}
              ariaLabel="upload"
              shape="default"
              size="medium"
              className="upload-icon-btn"
              testId="upload-button"
            />
            <ArrIconButton
              icon={<SaveIcon className="save-icon" />}
              onClick={() => {
                saveColumnDetails(
                  {
                    SelectedCustomColumns: visibleFields,
                    IsAscending: sortModel[0].sort,
                    SortingColumn: sortModel[0].field,
                    IsProjectActive: true,
                  },
                  {
                    onSuccess: () => {
                      setShowToast(true);
                      setToastStatuses((prev) => [...prev, ToastType.Success]);
                      setReportToastMessage(
                        t('domainDashboard.savedGridConfig')
                      );
                    },
                  }
                );
              }}
              ariaLabel="save"
              shape="default"
              size="medium"
              className="save-icon-btn"
              testId="save-columns"
            />
            <ArrIconButton
              icon={<AddFieldSortIcon className="add-field-sort-icon" />}
              onClick={(e) => {
                menuRef1.current?.open(e.currentTarget as HTMLElement);
              }}
              ariaLabel="add-field-sort"
              shape="default"
              size="medium"
              className="add-filed-sort-icon-btn"
              testId="column-switcher"
            />

            <ArrMenu
              id="columns-switcher"
              className="domain-admin-column-switcher"
              ref={menuRef1}
              items={allColumns
                .map((col, index) => ({
                  label: t(col.key),
                  id: col.id,
                  checked: visibleFields.includes(col.id),
                  onToggle: () => handleToggleColumn(col.id),
                }))
                .filter((item) => item.id !== 'ProjectID')}
              renderItem={(item) => {
                return (
                  <Box className="column-option-container">
                    <ArrCheckbox
                      checked={!!item.checked}
                      onChange={() => {
                        item?.onToggle?.();
                      }}
                      data-testid={`checkbox-${item.id}`}
                      aria-label={`select-cb-${item.id}`}
                    />
                    <Box>
                      <Typography
                        className="typo-text body2_size wrap-text text-headings"
                        data-testid={`domain-admin-dashboard-typography-menu-item-label-${item.id}`}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
            ></ArrMenu>
          </div>
        </div>
        <Box className="grid-main">
          <ArrDataGrid
            rows={rows}
            columns={getColumns}
            rowCount={gridData?.totalRecords}
            currentPage={pageNumber}
            itemsPerPage={itemsPerPage}
            pageSize={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            onPageChange={onPageChangeHandler}
            entityType={'Projects'}
            primaryKey="ProjectLabel"
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            className="domain-admin-dashboard-grid"
            noRowsOverlay={NoRowsOverlay}
            showPerPageOptions={true}
            pageSizeOptions={[25, 50, 100]}
            hideFooter={rows.length === 0}
            getRowId={(row) => row.ProjectID}
            loading={gridLoading}
            data-testid="domain-admin-grid"
            alwaysShowPagination
            disableColumnResize={false}
          />
        </Box>
      </div>
      {showCustomizeTagWidget && (
        <CustomizeTagWidget
          showDrawer={showCustomizeTagWidget}
          handleDrawer={setShowCustomizeTagWidget}
        />
      )}
      {showCustomizeReviewerProductivity && (
        <CustomizeReviewerProductivityWidget
          showDrawer={showCustomizeReviewerProductivity}
          handleDrawer={setShowCustomizeReviewerProductivity}
        />
      )}
      {showCustomizeReviewerProgress && (
        <CustomizeReviewerProgressWidget
          showDrawer={showCustomizeReviewerProgress}
          handleDrawer={setShowCustomizeReviewerProgress}
        />
      )}
      {showToast && (
        <ArrToastNotification
          message={
            toastStatuses[toastStatuses.length - 1] === ToastType.Success
              ? reportToastMessage
              : t('domainDashboard.failedSaveGridConfig')
          }
          onClose={handleToastClose}
          status={toastStatuses}
        />
      )}
      <ArrModal
        showModal={open}
        onClose={handleClose}
        title={t('domainDashboard.deactivateTitle')}
        description={t('domainDashboard.areYouSureDeactivate')}
        dataTestId="deactivate-modal"
        buttons={[
          {
            id: '1',
            label: t('common.no'),
            onClick: handleClose,
            variant: 'secondary',
            dataTestId: 'deactivate-modal-cancel-btn',
          },
          {
            id: '2',
            label: t('common.yes'),
            onClick: deactivateConfirm,
            variant: 'primary',
            dataTestId: 'deactivate-modal-confirm-btn',
          },
        ]}
      />
      {!!toastMessage && (
        <ArrToast
          id="status-toast"
          title={t('common.success')}
          type={ToastType.Success}
          message={t('domainDashboard.projectDeactivated')}
          duration={5000}
          dismissable
          onClose={() => {
            setToastMessage('');
          }}
          position="bottom-center"
        />
      )}
    </Box>
  );
};

export default DomainAdminDashboard;
